import { useState, useEffect, forwardRef } from 'react';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import MenuButtonsPortal from '../Menu/MenuButtonsPortal';

import BiButton from '../primitives/buttons/BiButton.primitive';
import InputField from '../shared/Fields/InputField';
import PickList, { PICKLIST_TYPES } from '../shared/Fields/PickList';

import refreshIcon from '../../assets/refresh.svg';
import close from '../../assets/close.svg';
import { Label } from '../Onboarding/OnboardingStyles';
import { chunk, isEmpty, isArray, isBoolean } from 'lodash';
import DatePicker from 'react-datepicker';
import Asterix from '../../assets/asterix.svg';
import { CalendarIcon } from '@heroicons/react/outline';
import { AppConstants } from '../../constants/AppConstants';
import moment from 'moment';

interface FilterCardProps {
  filtersList: any;
  hideFilter: Function;
  applyFilters: Function;
}

const FilterCard: React.FC<FilterCardProps> = ({ filtersList, hideFilter, applyFilters }) => {
  const { t } = useTranslation();

  const [defaultFilterList, setDefaultFilterList] = useState<any>(filtersList);
  const [defaultFilter, setDefaultFilter] = useState<any>(filtersList);
  const [resetKey, setResetKey] = useState<number>(0);

  const getInitialFilter = () => {
    const initialFilter: any = {};
    filtersList.forEach((filter: any) => {
      if (isArray(filter?.selectedFilter)) {
        if (filter?.selectedFilter?.length) {
          initialFilter[filter.id] = filter.selectedFilter;
        }
      } else {
        initialFilter[filter.id] = filter.selectedFilter || '';
      }
    });

    return initialFilter;
  };

  const [appliedFilter, setAppliedFilter] = useState<any>(getInitialFilter());

  const onPicklistChange = (
    item: any,
    filterId: any,
    isDependentFilter: any,
    fetchDependentFields: any,
    dependentChildId: any
  ) => {
    setAppliedFilter((prevState: any) => ({ ...prevState, [filterId]: item }));

    if (isDependentFilter) {
      //setAppliedFilter({ ...appliedFilter, [dependentChildId]: [] });
      setAppliedFilter((prevState: any) => ({ ...prevState, [dependentChildId]: [] }));
      fetchDependentFields(item);
    }
  };

  const onInputChange = (e: any, filterId: any) => {
    const val = e.target.value;
    setAppliedFilter((prevState: any) => ({ ...prevState, [filterId]: val }));
  };

  const onToggleChange = (val: any, filterId: any) => {
    setAppliedFilter((prevState: any) => ({ ...prevState, [filterId]: val }));
  };

  const resetDependentFilter = () => {
    filtersList.forEach((filter: any) => {
      const { isDependentFilter, fetchDependentFields, id } = filter;
      if (isDependentFilter) {
        if (fetchDependentFields) fetchDependentFields([]);
      }
    });
  };

  const onResetFilters = () => {
    resetDependentFilter();
    setAppliedFilter({});
    setResetKey((prevKey) => prevKey + 1);
  };

  const onApplyFilters = () => {
    applyFilters(appliedFilter);
    hideFilter(false);
  };

  const validateApplyFilter = () => {
    let toBeDisabled = true;
    const initialFilter = getInitialFilter();
    if (!isEmpty(initialFilter)) {
      return false;
    }
    if (isEmpty(appliedFilter)) {
      toBeDisabled = true;
    } else {
      const filtersApplied = Object.keys(appliedFilter);
      filtersApplied.forEach((key: any) => {
        if (appliedFilter[key].length || isBoolean(appliedFilter[key])) {
          toBeDisabled = false;
        }
      });
    }
    return toBeDisabled;
  };

  const DateInputField = forwardRef(
    ({ onClick, formError, disabled, placeholder, label, id, overrideValue }: any, ref) => (
      <InputField
        className="w-full"
        type="text"
        id={id}
        key={`${id}-${resetKey}`}
        label={label}
        placeholder={placeholder}
        fieldKey={''}
        overrideValue={overrideValue}
        required={false}
        formData={{}}
        setFormData={() => {}}
        handleChange={(e: any) => onInputChange(e, id)}
        formError={formError}
        disabled={disabled}
        onClick={onClick}
        ref={ref}
        onBlurhandler={(e: any) => {}}
        CustomIcon={CalendarIcon}
      />
    )
  );

  const getFilterField = (filterObj: any) => {
    const {
      component,
      type,
      selectedFilter,
      itemsList,
      label,
      fieldKey,
      fieldValues,
      id,
      disabled,
      fetchMoreData,
      hasMore = false,
      isDependentFilter,
      fetchDependentFields = () => {},
      dependentChildId = '',
      searchData = () => {},
      searchValue = '',
      searchable,
      searchPlaceholder = '',
      formData,
      setFormData,
      formError,
      displayDelimiter = ','
    } = filterObj;

    switch (component) {
      case 'picklist':
        return (
          <>
            <PickList
              fieldKey={fieldKey}
              required={false}
              fieldValues={typeof fieldValues === 'string' ? [fieldValues] : fieldValues}
              selectedItems={appliedFilter[id] ? appliedFilter[id] : []}
              onSelectedChange={(item: any) => {
                onPicklistChange(
                  item,
                  id,
                  isDependentFilter,
                  fetchDependentFields,
                  dependentChildId
                );
              }}
              items={itemsList}
              disabled={disabled}
              readOnly={false}
              fetchMoreData={(id: any) => fetchMoreData(id)}
              searchData={searchData}
              searchable={searchable}
              searchValue={searchValue}
              searchPlaceholder={searchPlaceholder}
              hasMore={hasMore}
              displayDelimiter={displayDelimiter}
              type={type !== 'single' ? PICKLIST_TYPES.MULTI : PICKLIST_TYPES.SINGLE}
            />
          </>
        );
        break;
      case 'input':
        return (
          <InputField
            key={`${id}-${resetKey}`}
            fieldKey={id}
            type={type}
            id={id}
            label={label}
            placeholder={searchPlaceholder}
            required={false}
            formData={appliedFilter[id] ? appliedFilter : {}}
            setFormData={() => {}}
            handleChange={(e: any) => onInputChange(e, id)}
            formError={{}}
            asterixFirst={false}
            disabled={false}
            allowSplChar={false}
            validateField={true}
            disableLabel={true}
          />
        );
        break;
      case 'datetime':
        return (
          <DatePicker
            selected={appliedFilter?.[id] ? new Date(appliedFilter[id]) : null}
            onChange={(date: any) =>
              setAppliedFilter((prevState: any) => ({
                ...prevState,
                [id]: new Date(date).toISOString()
              }))
            }
            shouldCloseOnSelect={false}
            showMonthDropdown
            showYearDropdown
            showTimeSelect
            scrollableYearDropdown
            yearDropdownItemNumber={100}
            calendarClassName={'text-base'}
            customInput={
              <DateInputField
                id={id}
                formError={{}}
                label={label}
                overrideValue={
                  appliedFilter?.[id]
                    ? moment(appliedFilter[id]).format(AppConstants.DEFAULT_DATE_FORMAT)
                    : ''
                }
              />
            }
          />
        );
        break;
      case 'boolean':
        return (
          <>
            <div className={`mt-3 flex h-full`}>
              <Switch
                className={`${disabled ? `disabled` : ``}`}
                toggle={appliedFilter[id] ? appliedFilter[id] : false}
                onClick={() => onToggleChange(!appliedFilter[id], id)}
                data-testid={'boolean-field'}
                id={'boolean'}
              >
                <ToggleButton toggle={appliedFilter[id]} />
              </Switch>
              <div className="ml-3">{appliedFilter[id] ? 'True' : 'False'}</div>
            </div>
          </>
        );
        break;
    }
  };

  const renderFields = (fields: any) => {
    let widthClass = fields.length > 1 ? `w-1/${fields.length}` : 'w-full';

    return fields.map((field: any) => {
      return (
        <div className={`flex ${field.width ? field.width : widthClass} flex-col pl-4`}>
          <div className="flex flex-row items-center">
            <Label>{field?.label && field.component !== 'datetime' ? t(field.label) : ''}</Label>
          </div>
          <div className="flex h-full items-center justify-start">{getFilterField(field)}</div>
        </div>
      );
    });
  };

  const getFilterFields = () => {
    const filterChunks = chunk(filtersList, 3);
    return filterChunks.map((filterFields) => {
      return <div className="mb-5 mt-5 flex">{renderFields(filterFields)}</div>;
    });
  };

  return (
    <>
      <div className="flex h-full items-center justify-end">
        <BiButton
          className={'ml-1 mr-2 text-primary'}
          type="button"
          style={{ padding: '0' }}
          testId={t('T_RESET_FILTERS')}
          onClick={() => {
            onResetFilters();
          }}
        >
          {t('T_RESET_FILTERS')}
        </BiButton>
        <BiButton
          className={'w-1/7 bg-primary text-white'}
          type="button"
          testId={t('T_FILTER_APPLY')}
          onClick={() => {
            onApplyFilters();
          }}
          disabled={validateApplyFilter()}
        >
          {t('T_FILTER_APPLY')}
        </BiButton>
        <img
          src={close}
          alt="headerLeftIcon"
          className="ml-2 h-4 cursor-pointer"
          onClick={() => hideFilter(false)}
        />
      </div>
      {getFilterFields()}
    </>
  );
};

const Image = styled.img`
  width: 8px;
  height: 8px;
`;

const Switch = styled.div<{ toggle: boolean }>`
  width: 44px;
  height: 22px;
  background: ${({ toggle }) => (!toggle ? '#BFBFBF' : '#0074e0')};
  border-radius: 16px;
  position: relative;
  margin-top: 3px;
  transition: ease-out 0.5s;
  cursor: pointer;
  &.disabled {
    pointer-events: none;
    background-color: #bfbfbf;
  }
`;

const ToggleButton = styled.div<{ toggle: boolean }>`
  position: absolute;
  height: 18px;
  top: calc(50% - 18px / 2);
  background: #ffffff;
  border-radius: 16px;
  width: 16px;
  ${({ toggle }) => (!toggle ? `left: 2px;` : 'right: 2px;')}
`;

export default FilterCard;
